import { Mutex } from 'async-mutex';

import { getLocaleUtil } from '../../../main/utils/locale.util.ts';
import { getSentryUtil } from '../../../main/utils/monitoring/sentry.util.ts';

import {
  SpeechSuperCommandEnum,
  SpeechSuperCoreTypeEnum,
  SPEECH_SUPER_SDK,
  SPEECH_SUPER_CONNECTION_MANAGER_OPTIONS,
  EVALUATION_TIMEOUT,
  SpeechSuperEofEnum,
} from '../../constants/speechsuper.constants.ts';
import { SpeechSuperAssessmentCallbacks } from '../../types/speechsuper-grading.types.ts';
import {
  SpeechSuperAudioParams,
  SpeechSuperRequest,
  SpeechSuperRequestParams,
} from '../../types/speechsuper-request.types.ts';
import {
  SpeechSuperAssessmentResponseSchema,
  SpeechSuperAudioMessage,
  SpeechSuperIntermediateAssessmentResponse,
  SpeechSuperFinalAssessmentResponse,
} from '../../types/speechsuper-response.types.ts';

import { getSpeechSuperAppParamsProviderUtil } from './speechsuper-app-params-provider.util.ts';
import { type SpeechSuperConnectionPool } from './speechsuper-connection-pool.ts';

/**
 * Represents a WebSocket connection to SpeechSuper's API.
 * Handles connection lifecycle, message buffering, and assessment flow.
 *
 * Lifecycle:
 * 1. Creation: Establishes WebSocket connection
 * 2. Connection: Sends initial connect message and buffered messages
 * 3. Assessment: Handles start/stop of assessments
 * 4. Cleanup: Closes connection and releases resources
 */
export class SpeechSuperConnection {
  // Connection state
  private readonly socket: WebSocket;
  private isConnected = false;
  private lastUsed = Date.now();
  private connectedAt = Date.now();
  private mutex = new Mutex();

  // Message handling
  private readonly messageBuffer: (SpeechSuperAudioMessage | SpeechSuperRequest)[] = [];
  private onFinalMessageCallback?: (response: SpeechSuperFinalAssessmentResponse) => void;
  private onIntermediateMessageCallback?: (response: SpeechSuperIntermediateAssessmentResponse) => void;

  constructor(
    public readonly id: string,
    public readonly coreType: SpeechSuperCoreTypeEnum,
    private readonly pool: SpeechSuperConnectionPool,
    private readonly appParamsProvider = getSpeechSuperAppParamsProviderUtil()
  ) {
    this.socket = new WebSocket(this.endpoint);
    this.setupSocketHandlers();
  }

  // Connection properties
  private get endpoint(): string {
    return `wss://api.speechsuper.com/${this.coreType}`;
  }

  /**
   * Assessment Methods
   */

  /**
   * Starts an assessment with the given parameters.
   * Must be called before sending audio data.
   * @param request - The assessment request parameters
   * @param audio - The audio configuration
   * @param callbacks - Optional assessment callback handlers
   */
  async startAssessment(
    request: SpeechSuperRequestParams,
    audio: SpeechSuperAudioParams,
    callbacks?: SpeechSuperAssessmentCallbacks
  ): Promise<void> {
    this.onIntermediateMessageCallback = callbacks?.onIntermediateResult;
    const appParams = await this.appParamsProvider.getAssessmentParams();
    await this.send({
      cmd: SpeechSuperCommandEnum.START,
      param: {
        app: appParams,
        audio,
        request,
      },
    });
  }

  /**
   * Sends audio data through the connection.
   * If the connection is not ready, the message is buffered.
   * @param audio - The audio data to send
   */
  async sendAudio(audio: SpeechSuperAudioMessage): Promise<void> {
    await this.send(audio);
  }

  /**
   * Stops the assessment and returns the results.
   * Automatically releases the connection back to the pool.
   * @returns The assessment results
   */
  async stopAndGetResults(): Promise<SpeechSuperFinalAssessmentResponse | null> {
    await this.send({ cmd: SpeechSuperCommandEnum.STOP });
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        resolve(null);
      }, EVALUATION_TIMEOUT);

      this.onFinalMessageCallback = (response): void => {
        clearTimeout(timeout);
        this.lastUsed = Date.now();
        resolve(response);
        this.release();
      };
    });
  }

  /**
   * Connection Management Methods
   */

  /**
   * Checks if the connection is valid and can be used.
   * @param maxIdleTime - Maximum time in milliseconds the connection can be idle
   * @returns Whether the connection is valid and usable
   */
  isValid(maxIdleTime: number): boolean {
    return this.isConnected && this.socket.readyState === WebSocket.OPEN && Date.now() - this.lastUsed < maxIdleTime;
  }

  /**
   * Gets the last time this connection was used.
   * @returns Timestamp of last usage
   */
  getLastUsedTime(): number {
    return this.lastUsed;
  }

  /**
   * Closes the connection and cleans up resources.
   */
  async close(): Promise<void> {
    if (this.socket.readyState === WebSocket.OPEN) {
      console.log('Closing connection');
      await new Promise<void>((resolve) => {
        this.socket.onclose = (): void => resolve();
        this.socket.close();
      });
    }
  }

  /**
   * Private Methods
   */

  private async send(message: SpeechSuperRequest | SpeechSuperAudioMessage): Promise<void> {
    // We need to ensure that there are no race conditions when accessing the isConnected flag and the message buffer
    // Otherwise we could send messages out of order
    await this.bufferMessage(message);
    const isConnected = await this.safelyGetIsConnected();
    if (isConnected) {
      await this.sendAllBufferedMessages();
    }
    this.lastUsed = Date.now();
  }

  private async safelyGetIsConnected(): Promise<boolean> {
    await this.mutex.acquire();
    try {
      return this.isConnected;
    } finally {
      this.mutex.release();
    }
  }
  private async sendUnbuffered(message: SpeechSuperRequest | SpeechSuperAudioMessage): Promise<void> {
    if ('audioBlob' in message) {
      this.socket.send(await message.audioBlob.arrayBuffer());
    } else {
      this.socket.send(JSON.stringify(message));
    }
  }

  private async sendAllBufferedMessages(): Promise<void> {
    await this.mutex.acquire();
    try {
      while (this.messageBuffer.length > 0) {
        const message = this.messageBuffer.shift();
        if (message) {
          await this.sendUnbuffered(message);
        }
      }
    } finally {
      this.mutex.release();
    }
  }

  private async bufferMessage(message: SpeechSuperRequest | SpeechSuperAudioMessage): Promise<void> {
    await this.mutex.acquire();
    try {
      this.messageBuffer.push(message);
    } finally {
      this.mutex.release();
    }
  }

  private setupSocketHandlers(): void {
    this.socket.onopen = (): void => {
      this.handleOpen();
    };

    this.socket.onmessage = (event: MessageEvent<string>): void => {
      this.handleMessage(event.data);
    };

    this.socket.onerror = (event: Event): void => {
      this.handleError(event as unknown as Error);
    };

    this.socket.onclose = (): void => {
      this.handleClose();
    };
  }

  private async handleOpen(): Promise<void> {
    // We need to ensure that there are no race conditions when accessing the isConnected flag and the message buffer
    // Otherwise we could send messages out of order, resulting in a failed assessment
    await this.mutex.acquire();
    try {
      this.isConnected = true;
      this.connectedAt = Date.now();

      const appParams = await this.appParamsProvider.getConnectionParams();

      // Send initial connect message
      const connectMessage: SpeechSuperRequest = {
        cmd: SpeechSuperCommandEnum.CONNECT,
        param: {
          sdk: SPEECH_SUPER_SDK,
          app: appParams,
        },
      };

      this.sendUnbuffered(connectMessage);
    } finally {
      this.mutex.release();
    }
    this.sendAllBufferedMessages();
  }

  private handleMessage(data: string): void {
    try {
      const response = SpeechSuperAssessmentResponseSchema.parse(JSON.parse(data));
      if (response.eof === SpeechSuperEofEnum.ZERO) {
        this.onIntermediateMessageCallback?.(response);
      } else {
        this.onFinalMessageCallback?.(response);
      }
    } catch (error) {
      const err = error as Error;
      this.handleError(new Error(getLocaleUtil().select('learn:connection_parse_error', { message: err.message })));
    }
  }

  private handleError(error: Error): void {
    getSentryUtil().trackError(error, {
      connectionId: this.id,
      coreType: this.coreType,
    });
    this.isConnected = false;
    throw new Error(getLocaleUtil().select('learn:connection_error', { message: error.message }));
  }

  private handleClose(): void {
    if (!this.isConnected) {
      return;
    }
    this.isConnected = false;
    throw new Error(getLocaleUtil().select('learn:connection_closed'));
  }

  private release(): void {
    this.pool.releaseConnection(this.id);
    const expired = Date.now() - this.connectedAt > SPEECH_SUPER_CONNECTION_MANAGER_OPTIONS.MAX_CONNECTION_AGE;
    if (expired) {
      this.pool.releaseConnection(this.id);
      this.socket.close();
      this.pool.removeConnection(this.id);
    }
  }
}
